import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'

import { colors, mediaQueriesByDevice } from 'parameters'
import { RangeOptions } from '../Utils/types'
import useResize from 'lib/hooks/useResize'

interface Props<T> {
	name: string
	options: RangeOptions<T>
	userInput: T
	onChange: (key: string, value: number) => void
}

//TODO: are these the same as search filters?
export const InputRange = <T,>({ name, options, userInput, onChange }: Props<T>): JSX.Element => {
	const [state, setState] = useState<number>(options.defaultValue)

	const { width } = useResize()

	useEffect(() => {
		if (state > options.max(userInput)) {
			setState(options.max(userInput))
		}
		updateRange(name)
	}, [name, options, state, userInput])

	const updateRange = (rangeName: string): void => {
		const range: HTMLInputElement | null = document.querySelector(`input[name=${rangeName}]`)
		const bubble: HTMLDivElement | null = document.querySelector(`.bubble-${rangeName}`)
		if (!range) {
			return
		}
		if (!bubble) {
			return
		}
		const val = Number(range.value)
		const width = range.offsetWidth
		const min = range.min ? Number(range.min) : 0
		const max = range.max ? Number(range.max) : 100
		const left = ((val - min) / (max - min)) * (width - 16 - 16) + 16

		bubble.style.left = `${left}px`

		range.style.background = `linear-gradient(to right, ${colors.primaryGreen1} 0%, ${
			colors.primaryGreen1
		} ${left}px, #DEE2E6 ${((val - min) / (max - min)) * 100}%, #DEE2E6 100%)`
	}

	const handleRangeChange = (event: React.FormEvent<HTMLInputElement>): void => {
		const name = event.currentTarget.name
		const value = Number(event.currentTarget.value)
		setState(value)
		updateRange(name)
	}

	useEffect(() => {
		const sliders = Array.from(document.querySelectorAll('input'))
		sliders.map(slider => updateRange(slider.name))
	}, [width])

	const valueModifier = options.displayInThousands ? 1000 : 1
	const valueFormatter = (val: number) =>
		`${options.leadingChar}${(val / valueModifier).toLocaleString()}${options.trailingChar}`

	const minValue = valueFormatter(options.min)
	const maxValue = valueFormatter(options.max(userInput))
	const bubbleValue = valueFormatter(state)

	return (
		<SliderWrapper fullWidth={options.isFullWidth}>
			<Title>
				<SliderTitle>{options.title}</SliderTitle>
				{options.subtitle && <SliderSubTitle>{options.subtitle}</SliderSubTitle>}
			</Title>
			<RangeWrapper>
				<Slider
					name={name}
					type="range"
					min={options.min}
					max={options.max(userInput)}
					step={options.step}
					value={state}
					onChange={handleRangeChange}
					onMouseUp={() => onChange(name, state)}
					onTouchEnd={() => onChange(name, state)}
				/>

				<MinValue>{minValue}</MinValue>
				<MaxValue>{maxValue}</MaxValue>
				<CurrentValue className={`bubble-${name}`}>
					<span>{bubbleValue}</span>
				</CurrentValue>
			</RangeWrapper>
		</SliderWrapper>
	)
}

const SliderWrapper = styled.div<{ fullWidth?: boolean }>`
	display: flex;
	flex-direction: column;
	width: ${({ fullWidth }): string => (fullWidth ? '100%' : '48%')};
	margin-bottom: 65px;
	position: relative;
	${mediaQueriesByDevice.mobile} {
		width: 100%;
	}
`

const RangeWrapper = styled.div`
	width: 100%;
	align-self: flex-end;
	position: relative;
`

const Title = styled.div`
	display: flex;
	flex-direction: column;
	margin-bottom: 40px;
	${mediaQueriesByDevice.mobile} {
		margin-bottom: 15px;
	}
`

const SliderTitle = styled.span`
	font-size: 18px;
	font-weight: 700;
`

const SliderSubTitle = styled.span`
	font-size: 14px;
	margin-top: 8px;
	color: ${colors.secondaryDarkGrey1};
	${mediaQueriesByDevice.mobile} {
		width: 85%;
	}
`

const MinValue = styled.div`
	position: absolute;
	left: 0;
	bottom: -30px;
	color: ${colors.secondaryDarkGrey1};
	font-size: 14px;
`

const MaxValue = styled.div`
	position: absolute;
	right: 0;
	bottom: -30px;
	color: ${colors.secondaryDarkGrey1};
	font-size: 14px;
`

const CurrentValue = styled.div`
	position: absolute;
	top: -30px;
	${mediaQueriesByDevice.mobile} {
		left: unset !important;
		right: 0;
		top: -36px;
	}
	span {
		font-size: 14px;
		color: ${colors.black};
		font-weight: 700;
		display: block;
		width: 36px;
		text-align: center;
		position: absolute;
		left: 50%;
		transform: translate(-50%, 0);
		${mediaQueriesByDevice.mobile} {
			font-size: 18px;
			font-weight: 400;
			display: block;
			width: unset;
			position: unset;
			transform: unset;
		}
	}
`

const Slider = styled.input`
	display: flex;
	width: 100%;
	-webkit-appearance: none; /* Override default CSS styles */
	appearance: none;
	height: 8px;
	border-radius: 10px;
	background-color: #bdc4c9;
	outline: none; /* Remove outline */
	-webkit-transition: 0.2s; /* 0.2 seconds transition on hover */
	transition: opacity 0.2s;
	::-moz-range-thumb {
		width: 24px;
		height: 24px;
		border: 0;
		border-radius: 50%;
		background-color: ${colors.primaryGreen1};
		/* background: url('/images/slider_selector.svg'); */
		cursor: pointer;
	}
	::-webkit-slider-thumb {
		-webkit-appearance: none;
		appearance: none;
		width: 24px;
		height: 24px;
		border: 0;
		border-radius: 50%;
		background-color: ${colors.primaryGreen1};
		/* background: url('/images/slider_selector.svg'); */
		cursor: pointer;
	}
	::-moz-range-progress {
		height: 4px;
		background-color: ${colors.primaryGreen1};
		border-radius: 8px;
	}
	::-ms-fill-lower {
		height: 4px;
		background-color: ${colors.primaryGreen1};
		border-radius: 8px;
	}
`
